import { lazy } from 'react';
import i18next from 'i18next';
import permissions from '@shared/config/permissions';
import {
  AssessmentPerformanceIcon,
  ClaimsManagerIcon,
  DashboardIcon,
  PopulationHealthIcon,
} from '@shared/ui/icons';

const DashboardComponent = lazy(() => import('../../../../../pages/dashboard'));
// Population Health v3 dashboard — Variant C layout (KPI + filter chips
// + full-width chart). Entity hooks + page live in `pages/population-
// health`; Recharts + Redux slices are reused untouched from legacy.
const PopulationHealth = lazy(() => import('../../../../../pages/population-health'));
// Assessment Performance — survey-completion analytics across offices.
// Gated on the dedicated `ASSESSMENT_PERFORMANCE_VIEW` permission
// (bit 53). Backend gates the same endpoint on the matching
// `QHSPermissionType.ASSESSMENT_PERFORMANCE_VIEW`. Until ops grants
// bit 53 to roles in production, the dashboard is hidden from the
// sidebar and direct URLs redirect via ProtectedRoute.
const AssessmentPerformance = lazy(() =>
  import('../../../../../pages/assessment-performance')
);
// Claims Manager — portfolio-level financial performance dashboard.
// Gated on the dedicated CLAIMS_MANAGER_VIEW bit (60) since
// 2026-08-21; Phase 1 rode on ADMIN_PATIENT_IMPORT.
const ClaimsManager = lazy(() =>
  import('../../../../../pages/claims-manager')
);
// L2 (Account Detail) — reached from a portfolio row click on L1.
// Same permission gate as L1; hidden from the sidebar (nav item is
// only the L1 root). Breadcrumb resolves the account name lazily
// from the loaded payload so the crumb reads "Sole Health Medical
// Center" instead of the uuid.
const ClaimsManagerAccount = lazy(() =>
  import('../../../../../pages/claims-manager-account')
);
// L3 (CPT Detail) — reached from a CPT-Mix row click on L2. Same
// permission gate as L1/L2; sidebar-hidden. Breadcrumb shows the
// CPT code itself since the friendly description isn't in a route-
// level store — the header row on the page renders both.
const ClaimsManagerCpt = lazy(() =>
  import('../../../../../pages/claims-manager-cpt')
);

const DASHBOARD_ROUTES_ROOT = {
  path: '/:currentAccountUuid/dashboard',
  exact: true,
  additional: {
    component: DashboardComponent,
    title: i18next.t('titles:dashboard'),
    protected: true,
    icon: DashboardIcon,
    permission: permissions.DASHBOARD_GET,
    disabledId: 'dashboard',
    breadcrumb: {
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.dashboard || 'Dashboard',
    },
  },
};

// No `permission` key, deliberately — not an oversight and not a
// forgotten constant.
//
// Customer request: Population Health is open to everyone and is the
// page a user lands on after signing in. `checkPermission` returns true
// for a missing permission, so omitting the key is what opens the route,
// the sidebar item and the post-login landing (see
// `getDefaultRouteForAccount` in UserProvider, which asks this very
// route whether the user may see the page).
//
// It used to carry `ADMIN_PATIENT_IMPORT` (bit 37) — a broad uploads/
// admin bit shared with Bot Activities, Uploads and CSV export, which
// this page has nothing to do with. The backend never asked for that bit
// here either: every endpoint behind the page is gated on `SURVEYS_GET`
// in `AlertsAuthenticationOptions.cs`. The two sides disagreed, and the
// stricter client-side bit is what hid the page.
//
// The remaining gate is `disabledId` below: an account with
// `populationHealth` in `disabledFeatures` still does not see it.
const DASHBOARD_ROUTES_POPULATION_HEALTH = {
  path: '/:currentAccountUuid/populationHealth',
  exact: true,
  additional: {
    component: PopulationHealth,
    title: i18next.t('titles:populationHealth'),
    protected: true,
    icon: PopulationHealthIcon,
    disabledId: 'populationHealth',
    breadcrumb: {
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.populationHealth || 'Population Health',
    },
  },
};

const DASHBOARD_ROUTES_ASSESSMENT_PERFORMANCE = {
  path: '/:currentAccountUuid/assessment-performance',
  exact: true,
  additional: {
    component: AssessmentPerformance,
    title: i18next.t('titles:assessmentPerformance'),
    protected: true,
    icon: AssessmentPerformanceIcon,
    permission: permissions.ASSESSMENT_PERFORMANCE_VIEW,
    disabledId: 'assessmentPerformance',
    breadcrumb: {
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.assessmentPerformance || 'Assessment Performance',
    },
  },
};

const DASHBOARD_ROUTES_CLAIMS_MANAGER = {
  path: '/:currentAccountUuid/claims-manager',
  exact: true,
  additional: {
    component: ClaimsManager,
    title: i18next.t('titles:claimsManager'),
    protected: true,
    icon: ClaimsManagerIcon,
    permission: permissions.CLAIMS_MANAGER_VIEW,
    disabledId: 'claimsManager',
    breadcrumb: {
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.claimsManager || 'Claims Manager',
    },
  },
};

const DASHBOARD_ROUTES_CLAIMS_MANAGER_ACCOUNT = {
  path: '/:currentAccountUuid/claims-manager/:targetAccountUuid',
  exact: true,
  additional: {
    component: ClaimsManagerAccount,
    title: i18next.t('titles:claimsManager'),
    protected: true,
    permission: permissions.CLAIMS_MANAGER_VIEW,
    disabledId: 'claimsManager',
    parentRoute: DASHBOARD_ROUTES_CLAIMS_MANAGER,
    breadcrumb: {
      label: ({ state }) =>
        state.claimsManager?.accountDetail?.account?.businessName || 'Account Detail',
    },
  },
};

const DASHBOARD_ROUTES_CLAIMS_MANAGER_CPT = {
  path: '/:currentAccountUuid/claims-manager/:targetAccountUuid/cpt/:cptCode',
  exact: true,
  additional: {
    component: ClaimsManagerCpt,
    title: i18next.t('titles:claimsManager'),
    protected: true,
    permission: permissions.CLAIMS_MANAGER_VIEW,
    disabledId: 'claimsManager',
    parentRoute: DASHBOARD_ROUTES_CLAIMS_MANAGER_ACCOUNT,
    breadcrumb: {
      label: ({ params }) => (params?.cptCode ? `CPT ${params.cptCode}` : 'CPT Detail'),
    },
  },
};

export const DASHBOARD_ROUTES = {
  // ------- For child accounts ------
  // ...

  // ------- For current accounts ------
  root: DASHBOARD_ROUTES_ROOT,
  populationHealth: DASHBOARD_ROUTES_POPULATION_HEALTH,
  assessmentPerformance: DASHBOARD_ROUTES_ASSESSMENT_PERFORMANCE,
  claimsManager: DASHBOARD_ROUTES_CLAIMS_MANAGER,
  claimsManagerAccount: DASHBOARD_ROUTES_CLAIMS_MANAGER_ACCOUNT,
  claimsManagerCpt: DASHBOARD_ROUTES_CLAIMS_MANAGER_CPT,
};
